import { useState, useEffect, useCallback } from "react";
import type { GoogleDriveFile } from "@/lib/hooks/google-drive/use-google-drive-browser";
import { toast } from "sonner";

interface UseGoogleDriveLoadMoreProps {
  result: {
    files?: GoogleDriveFile[];
    nextPageToken?: string;
  } | undefined;
  viewMode: "drives" | "files";
  currentParentId: string | undefined;
  currentDriveId: string | undefined;
  email: string | undefined;
  isSecondAccount: boolean;
  accessToken: string | undefined;
}

export function useGoogleDriveLoadMore({
  result,
  viewMode,
  currentParentId,
  currentDriveId,
  email,
  isSecondAccount,
  accessToken,
}: UseGoogleDriveLoadMoreProps) {
  const [nextPageToken, setNextPageToken] = useState<string | undefined>(undefined);
  const [accumulatedFiles, setAccumulatedFiles] = useState<GoogleDriveFile[] | null>(null);

  useEffect(() => {
    if (!result) return;
    setNextPageToken(result.nextPageToken);
    if (viewMode === "files" && result.files) {
      setAccumulatedFiles(result.files);
    }
  }, [result?.nextPageToken, result?.files, viewMode]);

  useEffect(() => {
    setAccumulatedFiles(null);
    setNextPageToken(undefined);
  }, [currentParentId, currentDriveId, viewMode]);

  const loadMore = useCallback(async () => {
    if (!nextPageToken || !accessToken) return;

    const queryParams = new URLSearchParams();
    if (currentParentId) queryParams.set("parentId", currentParentId);
    if (currentDriveId) queryParams.set("driveId", currentDriveId);
    queryParams.set("action", viewMode === "drives" ? "drives" : "list");
    if (email) queryParams.set("email", email);
    if (isSecondAccount) queryParams.set("isSecondAccount", "true");
    queryParams.set("pageToken", nextPageToken);
    queryParams.set("pageSize", "100");

    const resp = await fetch(`/api/unifiedtime/getDrive/browse?${queryParams.toString()}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (!resp.ok) {
      const errorData = await resp.json();
      toast.error(errorData.error || "Failed to load more");
      return;
    }

    const more = await resp.json();
    setNextPageToken(more.nextPageToken);
    if (viewMode === "files") {
      setAccumulatedFiles((prev) => [...(prev || []), ...(more.files || [])]);
    }
  }, [
    nextPageToken,
    accessToken,
    currentParentId,
    currentDriveId,
    viewMode,
    email,
    isSecondAccount,
  ]);

  return {
    loadMore,
    nextPageToken,
    accumulatedFiles,
  };
}
